import * as React from 'react'
import { useLocation } from 'react-router-dom'

import usePageInfo from '../../hooks/usePageInfo'
import { useZustand } from '../../hooks/useZustand'
import { useBootstrapStore } from '../../stores/useBootstrapStore'
import {
    useContextsStore,
    selectIsOwnView,
} from '../../stores/useContextsStore'
import { isKeywordGraphApp } from '../../appVariant'
import {
    useOnboardingStore,
    selectGuideWaitsForPersona,
} from '../../stores/useOnboardingStore'
import {
    getBootNarrationUser,
    clearNarrationStartedAt,
} from '../../stores/onboardingStorage'
import {
    API_PAGE_COPY,
    API_PAGE_SPOTLIGHT,
    BUILDER_BRING_PRECURSOR_SIGNAL,
    PERSONAS,
    STAGES,
    STAGE_PRECURSOR_SIGNALS,
    getStageFlow,
    getStageSpotlight,
    getStepNames,
    graphTypeFromPathname,
    resolveCopy,
} from './copy'
import GraphTopicSpotlight from './GraphTopicSpotlight'
import GuideToast from './GuideToast'
import NarratedBuild from './NarratedBuild'
import Spotlight from './Spotlight'
import useGuideEligibility from './useGuideEligibility'
import useGuideObservers from './useGuideObservers'
import useOffGraphObservers from './useOffGraphObservers'
import useIntroHold from './useIntroHold'

// How long the first graph stands alone after the narration before the guide
// steps in — long enough to look around, even start something.
const INTRO_HOLD_MS = 10000

// The service pages whose stages complete off the graph: the first steps
// (connect, bring) show here, the loop itself needs an own graph page.
const SERVICE_PAGES = ['apps', 'api-access']
const OPENING_STAGES = [STAGES.CONNECT, STAGES.BRING]

/**
 * First-session guide: a toast walking a new user through one loop, in the
 * vocabulary of the persona they picked (PersonaToast asks first; copy.js
 * branches on it). Mounted once in App.jsx, self-gating by route. It
 * observes, it never performs: real actions advance the stages
 * (useGuideObservers on the graph page, useOffGraphObservers on the service
 * pages; docs/onboarding/first-session-guide.md). Eligibility and completion
 * are useGuideEligibility's call.
 */
export default function OnboardingGuide() {
    const { location } = usePageInfo()
    const { pathname } = useLocation()
    useGuideEligibility()

    const status = useOnboardingStore(state => state.status)
    const stage = useOnboardingStore(state => state.stage)
    const persona = useOnboardingStore(state => state.persona)
    const waitingForPersona = useOnboardingStore(selectGuideWaitsForPersona)
    const upgradeReminderOpen = useOnboardingStore(
        state => state.upgradeReminderOpen
    )
    const importNarrationActive = useOnboardingStore(
        state => state.importNarrationActive
    )
    const storageUsername = useOnboardingStore(state => state.storageUsername)
    // The X only collapses; permanent dismiss() waits for a "don't show again" control.
    const setCollapsed = useOnboardingStore(state => state.setCollapsed)
    const collapsed = useOnboardingStore(state => state.collapsed)
    const apiKeyCount = useOnboardingStore(state => state.apiKeyCount)

    const user = useBootstrapStore(state => state.user)
    const isOwnView = useContextsStore(selectIsOwnView)
    const graphOwnerResolved = useContextsStore(
        state => !!state.currentGraphOwner
    )
    const openGraphIsEmpty = useZustand(
        state =>
            state.loadedStatements &&
            !(state.statements[state.currentContext] || []).length
    )

    // Progress inside a stage (the gap dialog opened, the agent's graph
    // appeared) swaps the action line only.
    const precursorSignal =
        persona === PERSONAS.BUILDER && stage === STAGES.BRING
            ? BUILDER_BRING_PRECURSOR_SIGNAL
            : STAGE_PRECURSOR_SIGNALS[stage]
    const precursorObserved = useOnboardingStore(
        state => !!state.signals[precursorSignal]
    )

    // Own graph pages only: activity on someone else's graph never advances this guide.
    const { aiAnswerState } = useGuideObservers({
        enabled: status === 'active' && location === 'context' && isOwnView,
    })
    const onServicePage = SERVICE_PAGES.includes(location)
    const { agentGraph } = useOffGraphObservers({
        enabled: status === 'active' && onServicePage,
    })
    const introHeld = useIntroHold()

    const [setupCardOpen, setSetupCardOpen] = React.useState(false)

    // Hand-over from App.jsx's boot overlay: kept up on the shared clock until the graph loads.
    const [bootNarrationUser, setBootNarrationUser] = React.useState(() =>
        getBootNarrationUser()
    )

    // Tear the overlay down when the resolved session contradicts the synchronous localStorage guess.
    React.useEffect(() => {
        if (!bootNarrationUser) return
        if (user && !user.isAuthenticated) {
            setBootNarrationUser(null)
            return
        }
        if (user?.isAuthenticated && user.username !== bootNarrationUser) {
            setBootNarrationUser(null)
            return
        }
        if (
            storageUsername === bootNarrationUser &&
            (status !== 'active' || stage !== STAGES.BRING)
        ) {
            setBootNarrationUser(null)
            return
        }
        if (graphOwnerResolved && !isOwnView) {
            setBootNarrationUser(null)
        }
    }, [
        bootNarrationUser,
        user,
        storageUsername,
        status,
        stage,
        graphOwnerResolved,
        isOwnView,
    ])

    const variant = isKeywordGraphApp() ? 'keywordgraph' : 'infranodus'
    const graphType = graphTypeFromPathname(pathname)
    const flow = getStageFlow(persona)
    const steps = getStepNames(persona)
    const copyFor = forStage =>
        resolveCopy(forStage, { variant, graphType, persona })

    const handleNarrationDone = () => {
        clearNarrationStartedAt()
        const store = useOnboardingStore.getState()
        store.revealGraph()
        if (store.status === 'active' && store.stage === STAGES.BRING) {
            const index = flow.indexOf(STAGES.BRING)
            store.holdIntro(INTRO_HOLD_MS)
            store.advanceTo(flow[Math.min(index + 1, flow.length - 1)], {
                advancedBy: 'narration',
            })
        }
        setBootNarrationUser(null)
    }

    // Rendered before any other gate so the overlay never flashes away while the
    // stores settle. `canComplete` waits for hydration: completing against a
    // still-'unknown' store spends the one-shot without handing over.
    if (bootNarrationUser) {
        return (
            <NarratedBuild
                onDone={handleNarrationDone}
                canComplete={storageUsername === bootNarrationUser}
            />
        )
    }

    const openingToast = forStage => {
        const copy = copyFor(forStage)
        const spotlight = getStageSpotlight(forStage, persona)
        // Full loads, not router navigation: a graph page boots from the
        // server (and runs the boot narration from its first paint).
        const handleAction = () => {
            // The agent's graph is in: the button opens it.
            if (precursorObserved && agentGraph) {
                window.location.assign(
                    `/${user.username}/${agentGraph.name}/edit`
                )
                return
            }
            if (copy.actionTo) {
                window.location.assign(copy.actionTo)
                return
            }
            setCollapsed(true)
        }
        const actionLabel =
            precursorObserved && agentGraph
                ? 'Open the graph'
                : copy.actionButton || copy.action
        return (
            <>
                {spotlight ? (
                    <Spotlight
                        selector={spotlight.selector}
                        testId="guide-spotlight"
                        dim={!collapsed}
                    />
                ) : null}
                <GuideToast
                    copy={copy}
                    steps={steps}
                    actionLabel={actionLabel}
                    onAction={handleAction}
                    onDismiss={() => setCollapsed(true)}
                    observed={precursorObserved}
                />
            </>
        )
    }

    if (location === 'api-access') {
        // The builder's second step reads here too: the key was just made on
        // this page, and the next thing happens in the agent.
        if (
            status === 'active' &&
            persona === PERSONAS.BUILDER &&
            stage === STAGES.BRING
        ) {
            return openingToast(STAGES.BRING)
        }
        // Gated on the key, not the stage: a finished user still has nothing connected without one.
        const guideBelongsToThisUser = status === 'active' || status === 'done'
        if (!guideBelongsToThisUser || apiKeyCount !== 0) return null
        return (
            <>
                <Spotlight
                    selector={API_PAGE_SPOTLIGHT}
                    testId="guide-spotlight"
                    dim={!collapsed}
                />
                <GuideToast
                    copy={API_PAGE_COPY}
                    steps={steps}
                    actionLabel={API_PAGE_COPY.actionButton}
                    onAction={() => setCollapsed(true)}
                    onDismiss={() => setCollapsed(true)}
                />
            </>
        )
    }

    if (status !== 'active') return null

    // Import POST in flight: cover the dialog. Success navigates away; ImportingNotice cancels on failure.
    if (importNarrationActive) {
        return <NarratedBuild canComplete={false} onDone={() => {}} />
    }

    if (location === 'apps') {
        if (!OPENING_STAGES.includes(stage)) return null
        // The persona question (PersonaToast) goes first; its answer picks the
        // flow. The upgrade reminder that may follow it holds the slot too.
        if (waitingForPersona || upgradeReminderOpen) return null
        return openingToast(stage)
    }

    if (location !== 'context' || !isOwnView) return null

    // A new empty graph opens its import dialog (FirstEntry); the guide waits
    // for content rather than covering it.
    if (openGraphIsEmpty) return null

    if (stage === STAGES.CONNECT) {
        // A graph page before the key exists: nothing to point at here.
        return null
    }

    if (stage === STAGES.BRING) {
        // Activated in another tab, or the agent's graph opened from its
        // reply: narrate now, then topics takes over.
        return <NarratedBuild onDone={handleNarrationDone} />
    }

    // The user meets their graph before the guide steps in.
    if (introHeld) return null

    const copy = copyFor(stage)
    const isExploreStage = stage === STAGES.EXPLORE
    const hasSetupCard = isExploreStage && !!copy.setupCard
    // No ring while an AI answer streams or is being read.
    const stageSpotlight =
        aiAnswerState === 'idle' ? getStageSpotlight(stage, persona) : null

    const handleAction = () => {
        if (isExploreStage) {
            if (hasSetupCard) {
                setSetupCardOpen(open => !open)
                return
            }
            // A link opens beside the app; either way the loop is done.
            if (copy.actionHref) {
                window.open(copy.actionHref, '_blank', 'noopener')
            }
            useOnboardingStore.getState().complete()
            return
        }
        // Acknowledge only; the observed action advances and reopens the toast.
        setCollapsed(true)
    }

    // "Maybe later" completes the loop so the last step is never left unchecked for good.
    const handleActionAlt = () => {
        if (isExploreStage) useOnboardingStore.getState().complete()
    }

    const actionLabel =
        hasSetupCard && setupCardOpen
            ? copy.actionButtonOpen
            : copy.actionButton || copy.action
    const actionAltLabel = isExploreStage ? copy.actionAlt : null

    return (
        <>
            <Spotlight
                selector={stageSpotlight?.selector}
                fallbackSelector={stageSpotlight?.fallbackSelector}
                round={stageSpotlight?.round}
                testId="guide-spotlight"
                dim={!collapsed}
            />
            <GraphTopicSpotlight dim={!collapsed} />
            <GuideToast
                copy={copy}
                steps={steps}
                actionLabel={actionLabel}
                actionAltLabel={actionAltLabel}
                onAction={handleAction}
                onActionAlt={handleActionAlt}
                onDismiss={() => setCollapsed(true)}
                showSetupCard={hasSetupCard && setupCardOpen}
                setupCardTab={copy.setupCard === 'obsidian' ? 'obsidian' : null}
                aiAnswerState={aiAnswerState}
                observed={precursorObserved}
            />
        </>
    )
}
